import {
    DurationSelector,
    WalletGatedButton,
    APYDisplay,
    DialogAwareContentContainer,
    DialogAwareButtonContainer,
} from "@/components";
import { useLockForm } from "@/hooks";
import { TokenInput } from "@/components";
import { formatTokenAmountForDisplay } from "@/utils";
import { type FormEvent, useCallback, useMemo } from "react";
import { LOCK_TOKEN_IDS, type LockTokenId } from "@/types";
import type { FieldError } from "react-hook-form";
import { isExpiredToken } from "@/config";
import { MobileTruncated } from "../text/MobileTruncated";
import { TbTrendingUp } from "react-icons/tb";
import { TrancheDisplay } from "./TrancheDisplay";
import { getLockTokenInputValidationError } from "./lockTokenInputValidation";

const lockTokenOptions = Object.values(LOCK_TOKEN_IDS)
    .filter((tokenId) => !isExpiredToken(tokenId))
    .sort((a, b) => a.localeCompare(b))
    .sort((a) => (a.startsWith("PT") ? 1 : -1)); // PT tokens last

interface LockFormProps {
    isLoading?: boolean;
    initialAmount?: number;
    initialDuration?: number;
    initialTokenId?: LockTokenId;
    isDialog?: boolean;
    tokenInputLabel?: string;
    hideDurationSelector?: boolean;
    hideProjectedEarnings?: boolean;
}

export function LockForm({
    isLoading,
    initialAmount,
    initialDuration,
    initialTokenId,
    isDialog,
    tokenInputLabel,
    hideDurationSelector,
    hideProjectedEarnings,
}: LockFormProps) {
    const {
        lockAmount,
        lockAmountError,
        duration,
        selectedTokenId,
        apy,
        isAPYLoading,
        projectedEarnings,
        submitLock,
        selectTokenId,
        setMaxAmount,
        isMaxDisabled,
        isAmountOverBalance,
    } = useLockForm({ initialAmount, initialDuration, initialTokenId });

    const handleTokenSelect = selectTokenId;
    const handleMaxClick = setMaxAmount;
    const tokenInputValidationError = useMemo(() => {
        if (!lockTokenOptions.includes(selectedTokenId as LockTokenId)) {
            return undefined;
        }
        return getLockTokenInputValidationError(selectedTokenId as LockTokenId, lockAmount.value);
    }, [lockAmount.value, selectedTokenId]);

    const handleSubmit = useCallback(
        async (e: FormEvent<HTMLFormElement>) => {
            e.preventDefault();

            if (tokenInputValidationError) {
                return;
            }
            await submitLock(e);
        },
        [submitLock, tokenInputValidationError],
    );

    const amountError: FieldError | undefined = useMemo(() => {
        if (lockAmountError) return lockAmountError;
        if (tokenInputValidationError) return { message: tokenInputValidationError, type: "min" };
        return undefined;
    }, [lockAmountError, tokenInputValidationError]);

    const isLockButtonDisabled = useMemo(() => {
        const lockAmountNumber = Number(lockAmount.value);
        if (!Number.isFinite(lockAmountNumber) || lockAmountNumber <= 0) {
            return true;
        }
        if (lockAmountError) {
            return true;
        }
        if (isAmountOverBalance) {
            return true;
        }
        return Boolean(tokenInputValidationError);
    }, [lockAmount.value, lockAmountError, tokenInputValidationError, isAmountOverBalance]);

    return (
        <form onSubmit={(e) => handleSubmit(e)}>
            <DialogAwareContentContainer isDialog={isDialog}>
                <TokenInput
                    label={tokenInputLabel}
                    amount={lockAmount}
                    amountError={amountError}
                    selectedTokenId={selectedTokenId}
                    handleTokenSelect={handleTokenSelect}
                    handleMaxClick={handleMaxClick}
                    tokenOptions={lockTokenOptions}
                    isMaxDisabled={isMaxDisabled}
                />

                <section className="flex flex-col gap-2 mt-4">
                    <span className="flex gap-1 items-center">
                        <TbTrendingUp className="text-secondary w-[20px] aspect-square h-[20px]" />
                        <span className="text-primary text-[18px] leading-[18px] font-medium">
                            Boost yield by choosing a longer unlock
                        </span>
                    </span>
                    <p className="text-dark text-[12px] leading-[16px] font-normal">
                        Your position remains locked until you begin unlock. The unlock completes after the selected
                        waiting period below. Locking longer earns more.
                    </p>
                </section>

                {!hideDurationSelector && <DurationSelector value={duration.value} onChange={duration.onChange} />}

                {!hideProjectedEarnings && (
                    <div className="mt-4 grid grid-cols-2 gap-2 md:items-end md:justify-between">
                        <APYDisplay
                            rate={apy}
                            isLoading={isAPYLoading}
                            tooltipText={`
                                Projected return for ${duration.value === 1 ? "a" : duration.value} week${
                                    duration.value === 1 ? "" : "s"
                                } lock based on trailing 7-day averages, current composition of locked depositor tranches, and current composition of infiniFi Portfolio. This figure does not represent or guarantee future results. Inclusive of fees.
                            `}
                        />
                        <div className="flex flex-col items-end gap-1">
                            <TrancheDisplay isJunior={true} />
                            <div className="flex flex-col md:flex-row text-dark text-[14px] md:items-end gap-1 justify-end">
                                <span>
                                    <MobileTruncated desktopText="Projected" mobileMaxChars={4} nEllipsis={1} />{" "}
                                    earnings:
                                </span>
                                <span>{formatTokenAmountForDisplay(projectedEarnings)} iUSD</span>
                            </div>
                        </div>
                    </div>
                )}
            </DialogAwareContentContainer>
            <div className="flex justify-center h-2" />

            <DialogAwareButtonContainer isDialog={isDialog}>
                <WalletGatedButton
                    className="text-lg py-2.5"
                    type="submit"
                    isLoading={isLoading}
                    fullWidth
                    variant="primary"
                    color="primary"
                    disabled={isLockButtonDisabled}
                >
                    Lock
                </WalletGatedButton>
            </DialogAwareButtonContainer>
        </form>
    );
}
